import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {withStyles} from "@mui/styles";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import moment from "moment-timezone";
import {withTranslation} from "react-i18next";
import ILocalAGGrid, {
  getDefaultColDef,
  createServerSideDatasource,
  withDateColumnFilter,
  applyReportFilterModel,
  attachReportServerSideDatasource,
} from "../../grid/iLocalAGGrid";
import getTypeByLang from "../../common/typeByLang";
import {generateCSV} from "../../../utils/GenerateCSV";
import {getDefaultDateRange, formatFromDateForAPI, formatToDateForAPI} from "../../../utils/datetime.utils";
import download from "../images/download-doc.svg";
import spinner from "../../assign-prescriptions/images/spinner.gif";


const styles = () => ({
  table: {
    minWidth: 1020,
  },
  tableWrapper: {
    overflowX: "auto",
  },
  padding10px: {
    padding: "10px",
  },
  spinner: {
    position: "absolute",
    background: "rgba(255,255,255,.4)",
    width: "100%",
    height: "100%",
    zIndex: 9999,
    top: 0,
    left: 0,
  },
  spinnerImage: {
    top: "calc(50% - 32.5px)",
    left: "calc(50% - 65px)",
    position: "absolute",
    width: "130px",
    userSelect: "none",
  },
  primaryButton: {
    background: "#6cca98",
    color: "#fff",
    "&:hover": {
      background: "#6cca98",
      color: "#fff",
    },
    "&:disabled": {
      color: "rgba(0, 0, 0, 0.38)",
      cursor: "not-allowed",
      background: "#E4E5E8",
    },
  },
});

class EnhancedTableHead extends React.Component {
  createSortHandler = (property) => (event) => {
    this.props.onRequestSort(event, property);
  };

  render() {
    const {order, orderBy, sortId, t} = this.props;
    const rows = [
      { id: "dispensing_location", label: t("Report.ControlledSubstance.DispensingLocation"), field: "store" },
      { id: "drugname", label: t("Report.ControlledSubstance.Item"), field: "drugname" },
      { id: "patient", label: t("Report.ControlledSubstance.Patient"), field: "firstname" },
      { id: "rxno", label: t("Report.ControlledSubstance.RxNumber"), field: "rxno" },
      { id: "dea_schedule", label: t("Report.ControlledSubstance.Schedule"), field: "dea_schedule" },
      { id: "pdrivers_license_no", label: t("Report.ControlledSubstance.IdNumber"), field: "pdrivers_license_no" },
      { id: "id_name", label: t("Report.ControlledSubstance.NameOnId"), field: "pfirst_name" },
      { id: "selectedstatecode", label: t("Report.ControlledSubstance.IssuingOrg"), field: "selectedstatecode" },
      { id: "expdate", label: t("Report.ControlledSubstance.IdExpiration"), field: "expdate" },
      { id: "card_type", label: t("Report.ControlledSubstance.IdType"), field: "card_type" },
      { id: "pickupdate", label: t("Report.ControlledSubstance.PickupDate"), field: "pickupdate" },
      { id: "care_team_member", label: t("Report.ControlledSubstance.CareTeam"), field: "username" },
      { id: "_id", label: t("Report.ControlledSubstance.IdImage"), sortable: false },
    ];

    return (
      <TableHead>
        <TableRow>
          {rows.map((row) => (
            <TableCell
              key={row.id}
              padding="default"
              sortDirection={orderBy === row.field ? order : false}
              style={{ padding: "10px", color: "rgba(0, 0, 0, 0.54)" }}
            >
              {row.sortable === false ? (
                <TableSortLabel style={{ cursor: "text" }}>{row.label}</TableSortLabel>
              ) : (
                <Tooltip
                  title={t("Global.Label.Filter.Sort")}
                  placement="bottom-start"
                  enterDelay={300}
                >
                  <TableSortLabel
                    active={orderBy === row.field && sortId === row.id}
                    direction={order}
                    onClick={this.createSortHandler(row.id)}
                  >
                    {row.label}
                  </TableSortLabel>
                </Tooltip>
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  }
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
  sortId: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

EnhancedTableHead = withTranslation()(EnhancedTableHead);

function getReportTimezone() {
  const stored = sessionStorage.getItem("timeZone");
  if (stored) return stored.substring(stored.lastIndexOf(" ") + 1);
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

function buildFilterRef() {
  return {
    store_ids: [],
    box_ids: [],
    searchtext: "",
    fromdate: "",
    todate: "",
    pageNo: 0,
    pageSize: 10,
    sortOrder: "desc",
    sortColumn: "pickup_date"|| null,
  };
}


function authHeaders() {
  return {
    Authorization:
      "Bearer " +
      JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken.jwtToken,
    "Content-Type": "application/json",
  };
}

/** Store + localized dispensing channel (kiosk, counter, bedside, curbside). */
function formatDispensingLocation(row, t) {
  const store = row.store || "";
  const channel =
    getTypeByLang(row.deliverytype, t) || row.deliverytype || row.box || "";
  if (store && channel) {
    return `${store} – ${channel}`;
  }
  return row.dispensing_location || store || channel;
}

/** Accept only loadable image URLs; drop deleted placeholders / junk. */
function normalizeReportImageUrl(url) {
  if (url == null) return "";
  const s = String(url).trim();
  if (!s || s === "null" || s === "undefined") return "";
  if (/image_deleted\.png/i.test(s)) return "";
  if (/^https?:\/\//i.test(s) || /^data:image\//i.test(s)) return s;
  return "";
}

/**
 * Resolve patient + ID image URLs.
 * New API returns patient_image_url + id_image_url.
 * Older APIs only return coalesced id_image_url (patient preferred) — show that as patient image.
 */
function resolveRowImages(row) {
  const patient = normalizeReportImageUrl(row.patient_image_url);
  const idCapture = normalizeReportImageUrl(row.id_image_url);
  const hasSplitPatientField = Object.prototype.hasOwnProperty.call(
    row,
    "patient_image_url"
  );

  if (!hasSplitPatientField) {
    return {
      patientImageUrl: idCapture,
      idImageUrl: "",
    };
  }

  // Avoid duplicating the same URL under both labels
  if (patient && idCapture && patient === idCapture) {
    return { patientImageUrl: patient, idImageUrl: "" };
  }

  return {
    patientImageUrl: patient,
    idImageUrl: idCapture,
  };
}

function rowHasAnyImage(row) {
  const images = resolveRowImages(row);
  return Boolean(images.patientImageUrl || images.idImageUrl);
}

function isAlreadySignedS3Url(url) {
  return /[?&](X-Amz-Signature|Signature)=/i.test(url || "");
}

/**
 * Sign private report image URLs for the preview dialog.
 * No-ops when URLs are already signed (new API page response) or endpoint is unavailable.
 */
async function signReportImageUrls(urls) {
  const list = Array.isArray(urls) ? urls.map((u) => u || "") : [];
  if (!list.some(Boolean)) return list;
  if (list.filter(Boolean).every(isAlreadySignedS3Url)) return list;

  try {
    const response = await fetch(
      process.env.REACT_APP_BASEURL + "signReportImages",
      {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ urls: list }),
      }
    );
    if (!response.ok) return list;
    const data = await response.json();
    if (!data || data.Success === false || !Array.isArray(data.Results)) {
      return list;
    }
    // Preserve index alignment with the request urls array.
    return list.map((original, i) =>
      original ? data.Results[i] || "" : ""
    );
  } catch (e) {
    return list;
  }
}

function ControlledSubstanceDispensedReport({ t, i18n, classes }) {
  const filterRef = useRef(buildFilterRef());
  const gridApiRef = useRef(null);
  const setRows = useState(null)[1];
  const setTotal = useState(0)[1];
  const [loading, setLoading] = useState(true);
  const [idDialog, setIdDialog] = useState({
    open: false,
    idImageUrl: "",
    patientImageUrl: "",
    idImageFailed: false,
    patientImageFailed: false,
    title: "",
  });
  const setIsDataEmpty = useState(true)[1];
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const convertToTimeZone = useCallback((requestDate) => {
    if (!requestDate) return "";
    const currentUserLang = localStorage.getItem("i18nextLng");
    let zone = sessionStorage.getItem("timeZone");
    const date = moment(new Date(requestDate));
    if (["null", null, "undefined", undefined].includes(zone)) {
      zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    const zoneName = zone.includes(" ") ? zone.substring(zone.lastIndexOf(" ") + 1) : zone;
    return currentUserLang === "en"
      ? date.tz(zoneName).format("MM/DD/YYYY hh:mm:ss A")
      : date.tz(zoneName).format("DD/MM/YYYY hh:mm:ss A");
  }, []);

  const refreshGrid = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;
    if (typeof api.refreshServerSideStore === "function") {
      api.refreshServerSideStore({ purge: true });
    } else if (typeof api.refreshServerSide === "function") {
      api.refreshServerSide({ purge: true });
    }
  }, []);

  const onPaginationChanged = useCallback((params) => {
    if (!params.api) return;
    const newPageSize = params.api.paginationGetPageSize();
    if (newPageSize && filterRef.current.pageSize !== newPageSize) {
      filterRef.current.pageSize = newPageSize;
    }
  }, []);

  const onGridReady = useCallback((params) => {
    gridApiRef.current = params.api;
    const datasource = createServerSideDatasource({
      filterBody: filterRef.current,
      applyFilterModel: (body, filterModel) => {
        applyReportFilterModel(body, filterModel, { dateColIds: ['pickupdate'] });
      },
      pageZeroBased: true,
      getPageSize: () => filterRef.current.pageSize || 10,
      fetchPage: (body) =>
        fetch(process.env.REACT_APP_BASEURL + "getControlledSubstanceDispensed", {
          method: "post",
          withCredentials: true,
          headers: authHeaders(),
          body: JSON.stringify(body),
        })
          .then((r) => r.json())
          .then((data) => {
            if (data && data.results) return data;
            if (Array.isArray(data)) return { results: data, total: data.length };
            return {
              results: (data && data.rows) || [],
              total: (data && data.total) || 0,
            };
          }),
      onSuccess: (results, total) => {
        setRows(results);
        setTotal(total || 0);
        setLoading(false);
        setIsDataEmpty(!results || results.length === 0);
        setIsButtonDisabled(!results || results.length === 0);
      },
      onError: () => setLoading(false),
    });
    attachReportServerSideDatasource(

      params,

      datasource,

      filterRef.current,

      'pickupdate'

    );
  }, []);

  const fetchGrid = useCallback(() => {
    if (gridApiRef.current) {
      refreshGrid();
      return;
    }
    const f = filterRef.current;
    setLoading(true);
    setRows(null);
    fetch(process.env.REACT_APP_BASEURL + "getControlledSubstanceDispensed", {
      method: "POST",
      withCredentials: true,
      headers: authHeaders(),
      body: JSON.stringify({
        ...f,
        timezone: getReportTimezone(),
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data && data.results && data.results.length > 0) {
          setRows(data.results);
          setTotal(data.total || 0);
          setIsDataEmpty(false);
          setIsButtonDisabled(false);
        } else {
          setRows(null);
          setTotal(data && typeof data.total === "number" ? data.total : 0);
          setIsDataEmpty(true);
          setIsButtonDisabled(true);
        }
      })
      .catch(() => {
        setRows(null);
        setTotal(0);
        setIsDataEmpty(true);
        setIsButtonDisabled(true);
      })
      .finally(() => setLoading(false));
  }, [refreshGrid]);

  useEffect(() => {
    const defaultDates = getDefaultDateRange();
    filterRef.current.fromdate = formatFromDateForAPI(defaultDates.fromDate);
    filterRef.current.todate = formatToDateForAPI(defaultDates.toDate);
    // Empty store_ids/box_ids: API scopes by account lineage (pickup-style).
    // Do not expand "All" into hundreds of box UUIDs — that 504s root users on Last 7 days.
    filterRef.current.store_ids = [];
    filterRef.current.box_ids = [];
    fetchGrid();
  }, [fetchGrid]);

  const exportCSV = () => {
    setLoading(true);
    fetch(process.env.REACT_APP_BASEURL + "CSV/ControlledSubstanceDispensedReport", {
      method: "POST",
      withCredentials: true,
      headers: authHeaders(),
      body: JSON.stringify({
        ...filterRef.current,
        timezone: getReportTimezone(),
      }),
    })
      .then((response) => {
        try {
          return response.json();
        } catch (err) {
          return null;
        }
      })
      .then((csvData) => {
        setLoading(false);
        const list =
          csvData && csvData.Results && csvData.Results.length > 0 ? csvData.Results : [];
        if (!list.length) {
          return;
        }
        const headers = [
          [
            "Dispensing location",
            "Item",
            "Patient first",
            "Patient last",
            "Rx #",
            "Fill",
            "Schedule",
            "ID number",
            "ID first name",
            "ID last name",
            "Issuing org",
            "ID expiration",
            "ID type",
            "Pickup date/time",
            "Care team member",
            "ID image URL",
            "Patient image URL",
          ].join(","),
        ];
        const lines = list.map((b) =>
          [
            formatDispensingLocation(b, t).replace(/,/g, " "),
            (b.drugname || "").replace(/,/g, " "),
            b.firstname || "",
            b.lastname || "",
            b.rxno || "",
            b.fillid != null ? String(b.fillid) : "",
            b.dea_schedule != null ? String(b.dea_schedule) : "",
            b.pdrivers_license_no || "",
            b.pfirst_name || "",
            b.plast_name || "",
            b.selectedstatecode || "",
            b.expdate != null ? String(b.expdate) : "",
            (b.card_type || "").replace(/,/g, " "),
            b.pickupdate ? convertToTimeZone(b.pickupdate) : "",
            (b.care_team_member || "").replace(/,/g, " "),
            b.id_image_url || "",
            b.patient_image_url || "",
          ].join(",")
        );
        generateCSV({
          data: [...headers, ...lines].join("\n"),
          fileName: "ControlledSubstanceDispensed.csv",
          fileType: "text/csv",
        });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const exportPDF = () => {
    setLoading(true);
    fetch(process.env.REACT_APP_BASEURL + "CSV/ControlledSubstanceDispensedReportPDF", {
      method: "POST",
      withCredentials: true,
      headers: authHeaders(),
      body: JSON.stringify({
        ...filterRef.current,
        timezone: getReportTimezone(),
        locale: i18n.language,
      }),
    })
      .then((response) => {
        const contentType = response.headers.get("Content-Type") || "";
        if (contentType.includes("application/pdf")) {
          return response.blob().then((blob) => ({ ok: true, blob }));
        }
        return response.text().then((text) => {
          try {
            const json = JSON.parse(text);
            if (json && json.Success && json.Results && (json.Results.html || json.Results.Html)) {
              return {
                ok: true,
                html: json.Results.html || json.Results.Html,
              };
            }
            return {
              ok: false,
              error:
                (json && json.Error) ||
                (json && json.Message) ||
                text ||
                t("Report.Picked.PdfError", { defaultValue: "Failed to generate PDF" }),
            };
          } catch (err) {
            return {
              ok: false,
              error: text || t("Report.Picked.PdfError", { defaultValue: "Failed to generate PDF" }),
            };
          }
        });
      })
      .then((result) => {
        setLoading(false);
        if (result.ok && result.blob) {
          const url = window.URL.createObjectURL(result.blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "ControlledSubstanceDispensed.pdf";
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          a.remove();
        } else if (result.ok && result.html) {
          const reportHtml = result.html;

          // Print returned HTML without opening a new tab (same as Picked Up Items).
          const iframe = document.createElement("iframe");
          iframe.style.position = "fixed";
          iframe.style.right = "0";
          iframe.style.bottom = "0";
          iframe.style.width = "0";
          iframe.style.height = "0";
          iframe.style.border = "0";
          iframe.style.visibility = "hidden";
          iframe.setAttribute("aria-hidden", "true");
          document.body.appendChild(iframe);

          const doc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document);
          if (doc) {
            doc.open();
            doc.write(reportHtml);
            doc.close();
          }

          setTimeout(() => {
            try {
              if (iframe.contentWindow) {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
              }
            } catch (err) {
              /* no-op */
            }
            setTimeout(() => {
              try {
                iframe.remove();
              } catch (err) {
                /* no-op */
              }
            }, 1000);
          }, 500);
        } else if (result.error) {
          alert(result.error);
        }
      })
      .catch((err) => {
        setLoading(false);
        alert(
          t("Report.Picked.PdfError", { defaultValue: "Failed to generate PDF" }) +
            (err && err.message ? ": " + err.message : "")
        );
      });
  };

  return (
    <Paper>
      {loading ? (
        <div className={classes.spinner}>
          <img className={classes.spinnerImage} src={spinner} alt="loading" />
        </div>
      ) : null}

      <div id="react-no-print">
        <Dialog
          open={idDialog.open}
          onClose={() => setIdDialog({ ...idDialog, open: false })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{idDialog.title}</DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
                justifyContent: "center",
                alignItems: "flex-start",
              }}
            >
              {idDialog.idImageUrl ? (
                <Box sx={{ textAlign: "center", flex: "1 1 280px", maxWidth: 420 }}>
                  <Box
                    component="div"
                    sx={{ mb: 1, fontWeight: 600, color: "rgba(0,0,0,0.6)", fontSize: "0.875rem" }}
                  >
                    {t("Report.ControlledSubstance.IdCaptureImage", {
                      defaultValue: "ID image",
                    })}
                  </Box>
                  {idDialog.idImageFailed ? (
                    <Box sx={{ py: 3, color: "rgba(0,0,0,0.54)" }}>
                      {t("Report.ControlledSubstance.ImageLoadFailed", {
                        defaultValue: "Unable to load image",
                      })}
                    </Box>
                  ) : (
                    <img
                      src={idDialog.idImageUrl}
                      alt={t("Report.ControlledSubstance.IdCaptureImage", {
                        defaultValue: "ID image",
                      })}
                      style={{ maxWidth: "100%", maxHeight: 420, objectFit: "contain" }}
                      onError={() =>
                        setIdDialog((prev) => ({ ...prev, idImageFailed: true }))
                      }
                    />
                  )}
                </Box>
              ) : null}
              {idDialog.patientImageUrl ? (
                <Box sx={{ textAlign: "center", flex: "1 1 280px", maxWidth: 420 }}>
                  <Box
                    component="div"
                    sx={{ mb: 1, fontWeight: 600, color: "rgba(0,0,0,0.6)", fontSize: "0.875rem" }}
                  >
                    {t("Report.ControlledSubstance.PatientImage", {
                      defaultValue: "Patient image",
                    })}
                  </Box>
                  {idDialog.patientImageFailed ? (
                    <Box sx={{ py: 3, color: "rgba(0,0,0,0.54)" }}>
                      {t("Report.ControlledSubstance.ImageLoadFailed", {
                        defaultValue: "Unable to load image",
                      })}
                    </Box>
                  ) : (
                    <img
                      src={idDialog.patientImageUrl}
                      alt={t("Report.ControlledSubstance.PatientImage", {
                        defaultValue: "Patient image",
                      })}
                      style={{ maxWidth: "100%", maxHeight: 420, objectFit: "contain" }}
                      onError={() =>
                        setIdDialog((prev) => ({ ...prev, patientImageFailed: true }))
                      }
                    />
                  )}
                </Box>
              ) : null}
              {!idDialog.idImageUrl && !idDialog.patientImageUrl ? (
                <Box sx={{ py: 4, color: "rgba(0,0,0,0.54)" }}>
                  {t("Report.ControlledSubstance.NoImageAvailable", {
                    defaultValue: "No image available",
                  })}
                </Box>
              ) : null}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setIdDialog({ ...idDialog, open: false })}
              color="primary"
              className={classes.primaryButton}
            >
              {t("Report.Picked.Temperature.CloseButton", { defaultValue: "Close" })}
            </Button>
          </DialogActions>
        </Dialog>
      </div>

            <Box component="div">
        <Grid container direction={'row'} alignItems={'center'} justifyContent={'flex-end'} spacing={2} sx={{ mt: 0, ml: 0, width: '100%', p: '0% 2% 1%' }}>
          <Grid item xs={12} md={12} sx={{ textAlign: 'end' }}>
            <Tooltip title={t("Report.Picked.ExportPdf", { defaultValue: "Export PDF" })}>
                          <IconButton
                            style={{ marginRight: "4px" }}
                            aria-label="ExportPDF"
                            disabled={isButtonDisabled}
                            onClick={exportPDF}
                          >
                            <PictureAsPdfIcon style={{ height: "28px", width: "28px" }} />
                          </IconButton>
                        </Tooltip>
            <Tooltip title={t("Report.Picked.ExportCSVTooltip")}>
                          <IconButton
                            style={{ marginRight: "0" }}
                            aria-label="ExportCSV"
                            onClick={exportCSV}
                            disabled={isButtonDisabled}
                          >
                            <img alt="download" src={download} style={{ height: "32px", width: "32px", cursor: "pointer" }} />
                          </IconButton>
                        </Tooltip>
          </Grid>
        </Grid>
      </Box>

      <Divider style={{ marginTop: "10px", height: "5px" }} />

      <div className={classes.tableWrapper}>
      <ILocalAGGrid
        columnDefs={[
          {
            field: "store",
            headerName: t("Report.ControlledSubstance.DispensingLocation"),
            colId: "dispensing_location",
            valueGetter: (p) => formatDispensingLocation(p.data || {}, t),
          },
          { field: "drugname", headerName: t("Report.ControlledSubstance.Item"), colId: "drugname" },
          {
            field: "firstname",
            headerName: t("Report.ControlledSubstance.Patient"),
            colId: "patient",
            valueGetter: (p) =>
              [p.data && p.data.firstname, p.data && p.data.lastname].filter(Boolean).join(" "),
          },
          {
            field: "rxno",
            headerName: t("Report.ControlledSubstance.RxNumber"),
            colId: "rxno",
            valueGetter: (p) => {
              const row = p.data || {};
              return row.fillid != null && row.fillid !== ""
                ? `${row.rxno} / ${row.fillid}`
                : row.rxno || "";
            },
          },
          {
            field: "dea_schedule",
            headerName: t("Report.ControlledSubstance.Schedule"),
            colId: "dea_schedule",
            valueGetter: (p) => {
              const row = p.data || {};
              return row.dea_schedule != null && row.dea_schedule !== ""
                ? String(row.dea_schedule)
                : "";
            },
          },
          { field: "pdrivers_license_no", headerName: t("Report.ControlledSubstance.IdNumber"), colId: "pdrivers_license_no" },
          {
            field: "pfirst_name",
            headerName: t("Report.ControlledSubstance.NameOnId"),
            colId: "id_name",
            valueGetter: (p) =>
              [p.data && p.data.pfirst_name, p.data && p.data.plast_name].filter(Boolean).join(" "),
          },
          { field: "selectedstatecode", headerName: t("Report.ControlledSubstance.IssuingOrg"), colId: "selectedstatecode" },
          { field: "expdate", headerName: t("Report.ControlledSubstance.IdExpiration"), colId: "expdate" },
          { field: "card_type", headerName: t("Report.ControlledSubstance.IdType"), colId: "card_type" },
          withDateColumnFilter({
            field: "pickupdate",
            headerName: t("Report.ControlledSubstance.PickupDate"),
            colId: "pickupdate",
            valueGetter: (p) =>
              p.data && p.data.pickupdate ? convertToTimeZone(p.data.pickupdate) : "",
          }),
          { field: "care_team_member", headerName: t("Report.ControlledSubstance.CareTeam"), colId: "care_team_member",
            valueGetter: (p) => (p.data && p.data.care_team_member) || (p.data && p.data.username) || "" },
          {
            field: "id_image_url",
            headerName: t("Report.ControlledSubstance.IdImage"),
            colId: "_id",
            filter: false,
            sortable: false,
            cellRenderer: (params) => {
              const row = params.data || {};
              if (!rowHasAnyImage(row)) return "—";
              return (
                <Tooltip title={t("Report.ControlledSubstance.ViewId")}>
                  <IconButton
                    size="small"
                    onClick={async () => {
                      const images = resolveRowImages(row);
                      setIdDialog({
                        open: true,
                        idImageUrl: images.idImageUrl,
                        patientImageUrl: images.patientImageUrl,
                        idImageFailed: false,
                        patientImageFailed: false,
                        title: t("Report.ControlledSubstance.IdPreviewTitle"),
                      });
                      const signed = await signReportImageUrls([
                        images.idImageUrl,
                        images.patientImageUrl,
                      ]);
                      setIdDialog((prev) => ({
                        ...prev,
                        idImageUrl: signed[0] || "",
                        patientImageUrl: signed[1] || "",
                        idImageFailed: Boolean(images.idImageUrl) && !signed[0],
                        patientImageFailed:
                          Boolean(images.patientImageUrl) && !signed[1],
                      }));
                    }}
                  >
                    <ImageSearchIcon />
                  </IconButton>
                </Tooltip>
              );
            },
          },
        ]}
        defaultColDef={getDefaultColDef()}
        rowModelType="serverSide"
        cacheBlockSize={filterRef.current.pageSize || 10}
        paginationPageSize={filterRef.current.pageSize || 10}
        paginationPageSizeSelector={[10, 20, 25]}
        rowHeight={40}
        getRowId={(params) =>
          `${(params.data && params.data.pmid) || ""}-${(params.data && params.data.rxid) || ""}-${(params.data && params.data.id) || ""}`
        }
        onGridReady={onGridReady}
        onPaginationChanged={onPaginationChanged}
      />

      </div>

    </Paper>
  );
}

ControlledSubstanceDispensedReport.propTypes = {
  t: PropTypes.func.isRequired,
  i18n: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles, { withTheme: true })(
  withTranslation()(ControlledSubstanceDispensedReport)
);



// WEBPACK FOOTER //
// ./src/components/reports/controlled-substance-dispensed/index.js